import {
    Autocomplete,
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import React, { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersion } from 'src/hooks/useQueryProductVersion';
import { useQueryProducts } from 'src/components/ProductSearchPage/hooks/useQueryProducts';
import { isValidPrd } from 'src/utils/productKeyValidation';

export type SourceProductOptionsMap = Record<string, string>;

export type SourceProductOptionsProps = {
    productKey: string;
    version?: number;
    options: SourceProductOptionsMap;
    onChangeProductKey: (prd: string) => void;
    onChangeVersion: (version?: number) => void;
    onChangeOptions: (opts: SourceProductOptionsMap) => void;
};

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root.Mui-focused': {
        backgroundColor: theme.palette.custom.lightBlue,
    },
}));

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
    height: '100%',
    position: 'absolute',
    right: theme.spacing(6),
    top: `calc(50% - ${theme.spacing(3)})`,
}));

const HeaderRow = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '4fr 3fr',
    gap: theme.spacing(2),
}));

const RowGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    alignItems: 'start',
    marginBottom: theme.spacing(2),
}));

export const SourceProductOptions = ({
    productKey,
    version,
    options,
    onChangeProductKey,
    onChangeVersion,
    onChangeOptions,
}: SourceProductOptionsProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const prdCandidate = (productKey ?? '').trim();
    const prdIsValid = isValidPrd(prdCandidate);

    const { data: productResp, isLoading } = useQueryProductVersion(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '', version },
        { enabled: prdIsValid, suspense: false },
    );

    const { data: productsData, isLoading: isLoadingProductName } = useQueryProducts(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '' },
        { enabled: prdIsValid, suspense: false },
    );

    const activeProduct = productResp?.result?.find((v) => v.isCurrent);
    const product = version
        ? productResp?.result?.find((v) => v.version === version)
        : activeProduct ?? productResp?.result?.[0];
    const productOptions = product?.options ?? [];
    const activeVersion = product?.version;

    const optionNames: string[] = [];
    const valuesByName: Record<string, string[]> = {};

    for (const opt of productOptions) {
        const o = opt as { name?: string; values?: unknown };
        const name = String(o.name ?? '');

        if (name) {
            optionNames.push(name);
            const rawVals = (o.values ?? []) as unknown[];

            valuesByName[name] = Array.isArray(rawVals)
                ? rawVals
                    .map((v) => (typeof v === 'string' ? v : String((v as { name?: unknown })?.name ?? '')))
                    .filter(Boolean)
                : [];
        }
    }
    optionNames.sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
    for (const k of optionNames) {
        valuesByName[k] = [...valuesByName[k]].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }

    const lastSigRef = useRef<string>('');

    useEffect(() => {
        if (!prdIsValid || !product) return;
        const sig = `${prdCandidate}|${product.version}|${optionNames.join('\u0001')}`;

        if (lastSigRef.current === sig) return;
        lastSigRef.current = sig;

        const next: SourceProductOptionsMap = {};

        for (const name of optionNames) {
            const vals = valuesByName[name] ?? [];
            const existing = options[name];

            next[name] = existing && vals.includes(existing) ? existing : (vals[0] ?? '');
        }
        onChangeOptions(next);
        if (version === undefined && activeVersion != null) onChangeVersion(activeVersion);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prdIsValid, prdCandidate, product?.version, optionNames.join('\u0001')]);

    const handleProductKeyChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChangeProductKey(e.target.value);
        onChangeVersion(undefined);
        onChangeOptions({});
        lastSigRef.current = '';
    };

    const handleProductVersionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const raw = e.target.value.trim();

        if (raw === '') {
            onChangeVersion(undefined);
            lastSigRef.current = '';
            return;
        }
        const parsed = Number(raw);

        if (Number.isFinite(parsed)) {
            onChangeVersion(parsed);
            lastSigRef.current = '';
        }
    };

    const setOptionValue = (name: string, value: string): void => {
        onChangeOptions({ ...options, [name]: value });
    };

    return (
        <Box>
            <HeaderRow>
                <StyledTextField
                    error={productKey.length > 0 && !prdIsValid}
                    helperText={productKey.length > 0 && !prdIsValid ? 'Invalid Product Key.' : ''}
                    InputProps={{
                        endAdornment: (
                            <StyledInputAdornment position="end">
                                {prdIsValid && isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            </StyledInputAdornment>
                        ),
                    }}
                    label="Source Product Key"
                    placeholder="PRD-1234567"
                    size="small"
                    value={productKey}
                    onChange={handleProductKeyChange}
                />
                <TextField
                    disabled={!prdIsValid}
                    label="Source Product Version"
                    size="small"
                    value={version ?? ''}
                    onChange={handleProductVersionChange}
                />
            </HeaderRow>
            {prdIsValid && (
                <Box sx={{
                    my: 2, display: 'flex', alignItems: 'center', gap: 1,
                }}
                >
                    {isLoadingProductName && (
                        <Skeleton animation="wave" height={24} width={200} />
                    )}
                    {!isLoadingProductName && productsData?.result?.[0]?.matching_versions?.[0]?.productName && (
                        <>
                            <Typography color="text.secondary" variant="body2">
                                {productsData.result[0].matching_versions[0].productName}
                            </Typography>
                            <Tooltip title="View in PIM">
                                <IconButton
                                    sx={{
                                        padding: 0.5, color: 'text.secondary', width: 16, height: 16,
                                    }}
                                    onClick={(): void => window.open(
                                        `https://pim.products.cimpress.io/merchant/products/${prdCandidate}/versions/${version ?? activeVersion}/overview`,
                                        '_blank',
                                    )?.focus()}
                                >
                                    <OpenInNew sx={{ fontSize: '16px', color: 'text.secondary' }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            )}
            {optionNames.length > 0 && (
                <Box>
                    <Typography fontWeight="medium" sx={{ mb: 1 }} variant="body2">
                        Source Product Options
                    </Typography>
                    {optionNames.map((name) => {
                        const vals = valuesByName[name] ?? [];
                        const current = options[name] ?? '';

                        return (
                            <RowGrid key={name}>
                                <TextField
                                    disabled
                                    label="Option Name"
                                    size="small"
                                    value={name}
                                />
                                <Autocomplete
                                    disableClearable
                                    options={vals}
                                    renderInput={(params): JSX.Element => (
                                        <TextField
                                            {...params}
                                            error={!current}
                                            helperText={!current ? 'Required' : ''}
                                            label="Option Value"
                                            size="small"
                                        />
                                    )}
                                    value={current || ''}
                                    onChange={(_, val): void => setOptionValue(name, (val as string) ?? '')}
                                />
                            </RowGrid>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};
